import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

const DIAGNOSTICS_ENABLED = true;
const assistantChatCallable = httpsCallable(functions, "assistantChat");

function createClientRequestId() {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function mapCallableCodeToStatus(code) {
  const normalized = String(code || "").toLowerCase();
  if (normalized.includes("unauthenticated")) return 401;
  if (normalized.includes("permission-denied")) return 403;
  if (normalized.includes("not-found")) return 404;
  if (normalized.includes("invalid-argument")) return 400;
  if (normalized.includes("failed-precondition")) return 409;
  if (normalized.includes("resource-exhausted")) return 429;
  if (normalized.includes("unavailable")) return 503;
  return 500;
}

function normalizeDiagnostics(error, clientRequestId) {
  const details = typeof error?.details === "object" && error?.details ? error.details : {};
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "Assistant request failed.");

  const sourceRaw = String(details?.source || "").toLowerCase();
  let source = "app";
  if (sourceRaw === "openai" || sourceRaw === "firebase" || sourceRaw === "app") {
    source = sourceRaw;
  } else if (
    code.includes("resource-exhausted") ||
    code.includes("unavailable") ||
    code.includes("deadline-exceeded")
  ) {
    source = "firebase";
  }

  const status = Number.isFinite(Number(details?.status))
    ? Number(details.status)
    : mapCallableCodeToStatus(code);

  return {
    source,
    status,
    openai_error_type: details?.openai_error_type || null,
    openai_error_code: details?.openai_error_code || null,
    request_id: details?.request_id || null,
    retry_after_ms: Number.isFinite(Number(details?.retry_after_ms))
      ? Number(details.retry_after_ms)
      : null,
    message: details?.message || message,
    client_request_id: details?.client_request_id || clientRequestId || null,
  };
}

export async function assistantChat(payload) {
  const clientRequestId = createClientRequestId();
  const safePayload = { ...(payload || {}) };
  // Do not allow client-selected models.
  delete safePayload.model;
  const requestPayload = {
    ...safePayload,
    clientRequestId,
  };

  try {
    const result = await assistantChatCallable(requestPayload);
    const data = result?.data || {};
    return {
      ...data,
      clientRequestId: data?.clientRequestId || clientRequestId,
    };
  } catch (error) {
    const diagnostic = normalizeDiagnostics(error, clientRequestId);
    if (DIAGNOSTICS_ENABLED) {
      console.error("[assistantChat] diagnostic", diagnostic);
    }
    error.diagnostic = diagnostic;
    throw error;
  }
}

export async function assistantFillPage(payload) {
  return assistantChat({
    ...(payload || {}),
    assistant_intent: "fill_page",
  });
}

export async function assistantLeaseExtractFields(payload) {
  const safePayload = { ...(payload || {}) };
  return assistantChat({
    propertyId: safePayload.propertyId,
    assistant_intent: "lease_extract_fields",
    leaseExtraction: {
      fileHash: safePayload.fileHash,
      extractionVersion: safePayload.extractionVersion,
      documentContext: safePayload.documentContext || "",
      pageImages: Array.isArray(safePayload.pageImages) ? safePayload.pageImages : [],
      unresolvedFields: Array.isArray(safePayload.unresolvedFields)
        ? safePayload.unresolvedFields
        : [],
    },
  });
}
